import React, { useEffect, useState, useRef } from 'react';

import {
  basisPointsToPercent,
  percentToBasisPoints,
} from 'features/deals/DealStages/EventActionCards/utils';
import { CashFlowModelingInputs } from 'features/drilldown/cashflows/CashFlowModelingInputs';
import { ChartSkeletonLoader } from 'features/pages/market-data/components/ChartSkeleton';
import { URLMapDeprecated } from 'features/pages/market-data/shared';

import { useQuery } from '@apollo/client';

import { StipulationFieldName, RateType } from '__generated__/globalTypes';

import { GetMarketData } from 'query/__generated__/GetMarketData';
import { GET_MARKET_DATA } from 'query/getMarketData';

import { useBiddingPricingContext } from '../BiddingPricingContext';
import MaturityData from './MaturityData';
import { YieldMatrixContentProps, YieldCurveData } from './types';
import { binarySearchMatrix, isWithinOrderedMatrix } from './utils';
import BidSummary, { BidSummaryInfo } from '../BidSummary/BidSummary';
import YieldToPriceChart from '../YieldToPriceChart';

export const YieldMatrixContent: React.FC<YieldMatrixContentProps> = ({
  selectedCarve,
  bidDetails,
  updateBidDetails,
  formValues,
  useSummaryData,
  onSubmit,
}) => {
  const [ycChartData, setYcChartData] = useState<YieldCurveData | null>(null);
  const { data } = useSummaryData(formValues);

  const chartParentRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(800);

  const [ycChartDataMap, setYcChartDataMap] = useState<URLMapDeprecated>({} as URLMapDeprecated);
  const [ycChartDataUrl, setYcChartDataUrl] = useState(
    formValues.rateType === RateType.RiskFree ? 'yc_chart_data.json' : 'sofr_yc_chart_data.json',
  );

  const {
    data: marketData,
    loading: marketDataLoading,
    error: marketDataError,
  } = useQuery<GetMarketData>(GET_MARKET_DATA, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.marketData) {
        const urlMap: URLMapDeprecated = {};
        Object.entries(data.marketData.urlMap).forEach(([key, value]) => {
          urlMap[key] = value;
        });
        setYcChartDataMap(urlMap);
      }
    },
  });

  useEffect(() => {
    if (ycChartDataUrl) {
      fetch(ycChartDataMap[ycChartDataUrl]).then((response) => {
        response.json().then((data: YieldCurveData) => {
          if (ycChartDataUrl === 'sofr_yc_chart_data.json') {
            // Create proper mapping from SOFR labels to desired display labels
            const sofrMapping = [
              { display: '1 Mo', sofrLabel: 'SOFR1M' },
              { display: '3 Mo', sofrLabel: 'SOFR3M' },
              { display: '6 Mo', sofrLabel: 'SOFR6M' },
              { display: '1 Yr', sofrLabel: 'SOFR1Y' },
              { display: '2 Yr', sofrLabel: 'SOFR2Y' },
              { display: '3 Yr', sofrLabel: 'SOFR3Y' },
              { display: '5 Yr', sofrLabel: 'SOFR5Y' },
              { display: '10 Yr', sofrLabel: 'SOFR10Y' },
              { display: '20 Yr', sofrLabel: 'SOFR20Y' },
              { display: '30 Yr', sofrLabel: 'SOFR30Y' },
            ];

            // Map data correctly
            const mappedLabels = sofrMapping.map((m) => m.display);
            const mappedSeries = {
              Last: sofrMapping.map((mapping) => {
                const sofrIndex = data.labels.indexOf(mapping.sofrLabel);
                return sofrIndex >= 0 ? data.series.Last[sofrIndex] : null;
              }),
              '1m Ago': sofrMapping.map((mapping) => {
                const sofrIndex = data.labels.indexOf(mapping.sofrLabel);
                return sofrIndex >= 0 ? data.series['1m Ago'][sofrIndex] : null;
              }),
              '6m Ago': sofrMapping.map((mapping) => {
                const sofrIndex = data.labels.indexOf(mapping.sofrLabel);
                return sofrIndex >= 0 ? data.series['6m Ago'][sofrIndex] : null;
              }),
            };

            const transformedData = {
              labels: mappedLabels,
              series: mappedSeries,
            };

            setYcChartData(transformedData);
          } else {
            setYcChartData(data);
          }
        });
      });
    }
  }, [ycChartDataUrl, ycChartDataMap]);

  const carveSummary = selectedCarve?.carve_summary;

  useEffect(() => {
    const updateChartWidth = () => {
      if (chartParentRef.current) {
        const parentWidth = chartParentRef.current.offsetWidth;
        setChartWidth(parentWidth - 310);
      }
    };

    updateChartWidth();
    window.addEventListener('resize', updateChartWidth);

    return () => {
      window.removeEventListener('resize', updateChartWidth);
    };
  }, [chartParentRef]);

  if (!bidDetails || !carveSummary) return null;

  const bidBasisPoints = bidDetails?.carve?.stipulations?.bid_basis_points || 0;
  const bidPx = basisPointsToPercent(bidBasisPoints);

  const bidSummary: BidSummaryInfo = {
    purchaseDollarPrice: 0,
  };

  if (
    data != null &&
    'price_yield_matrix' in data.cashFlows &&
    data.cashFlows.price_yield_matrix != null
  ) {
    const priceYieldMatrix = data.cashFlows.price_yield_matrix;
    // bidPrice is a decimal (e.g., 0.95 for 95%)
    const bidPrice = parseFloat(basisPointsToPercent(bidBasisPoints)) / 100;
    // carveSummary.unpaidBalance is in cents (from current_balance_cents)
    // Matrix values are in dollars, so calculate purchase price in dollars directly:
    // purchasePriceDollars = bidPrice (decimal) * unpaidBalance (cents) / 100 = dollars
    const purchasePriceDollars = (bidPrice * carveSummary.unpaidBalance) / 100;
    // Store purchase price in cents for bidSummary
    bidSummary.purchaseDollarPrice = purchasePriceDollars * 100;

    const spreadMatrix = priceYieldMatrix.spread_matrix;
    const yieldMatrix = priceYieldMatrix.yield_matrix;

    // Compare purchase price in dollars directly with matrix (matrix values are in dollars)
    if (isWithinOrderedMatrix(purchasePriceDollars, spreadMatrix)) {
      const spreadData = binarySearchMatrix(spreadMatrix, purchasePriceDollars);
      bidSummary.spread = spreadData.rate * 10_000;
      bidSummary.spreadDuration = spreadData.duration;
    }

    if (isWithinOrderedMatrix(purchasePriceDollars, yieldMatrix)) {
      const yieldData = binarySearchMatrix(yieldMatrix, purchasePriceDollars);
      bidSummary.netYield = yieldData.rate * 100;
      bidSummary.yieldDuration = yieldData.duration;
    }
  }

  const handlePointClick = (payload: any) => {
    const price = parseFloat(payload.price.toFixed(3));
    const basisPoints = percentToBasisPoints(price.toString());

    updateBidDetails(
      {
        ...bidDetails,
        carve: {
          ...bidDetails.carve,
          stipulations: {
            ...bidDetails.stipulations,
            bid_basis_points: basisPoints,
          },
        },
      },
      StipulationFieldName.bid_basis_points,
      basisPoints.toString(),
    );
  };

  return (
    <>
      <div
        style={{
          paddingBottom: 15,
          gap: 40,
          display: 'flex',
        }}
      >
        <CashFlowModelingInputs formValues={formValues} onSubmit={onSubmit} withServicingRate />
      </div>

      <MaturityData
        ycChartData={ycChartData}
        setYcChartDataUrl={setYcChartDataUrl}
        formValues={formValues}
        onCurveSelect={onSubmit}
      />

      <div className="flex flex-col gap-8 md:flex-row" ref={chartParentRef}>
        <div style={{ width: `${chartWidth}px` }} className="w-full">
          {data != null && 'price_yield_matrix' in data.cashFlows ? (
            <YieldToPriceChart
              chartData={data.cashFlows.price_yield_matrix}
              upb={carveSummary.unpaidBalance}
              bidPx={bidPx}
              width={chartWidth - 25}
              onPointClick={handlePointClick}
            />
          ) : (
            <ChartSkeletonLoader height={500} width={chartWidth - 25} />
          )}
        </div>

        <div className="min-w-[300px] pr-4">
          <BidSummary
            netCoupon={carveSummary.netCoupon}
            unpaidBalance={carveSummary.unpaidBalance}
            netYield={bidSummary.netYield}
            spread={bidSummary.spread}
            yieldDuration={bidSummary.yieldDuration}
            spreadDuration={bidSummary.spreadDuration}
            participationPercent={Number(bidDetails.participationPercent.value)}
            bidBasisPoints={bidDetails.carve.stipulations?.bid_basis_points}
            servicingRate={Number(bidDetails.stipulations?.servicingRate.value)}
            defaultAssumptionType={bidDetails.defaultAssumptionType}
            cdr={bidDetails.cdr}
            prepaymentAssumptionType={bidDetails.prepaymentAssumptionType}
            cpr={bidDetails.cpr}
          />
        </div>
      </div>
    </>
  );
};

export default YieldMatrixContent;
